package com.rosario.juridico.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.NavDeepLink
import androidx.navigation.NamedNavArgument
import androidx.navigation.NavGraphBuilder
import androidx.navigation.compose.composable
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.platform.FirebaseProxy

/**
 * Componente de protección y enrutamiento ("Auth Guard") para el Marketplace Legal.
 * Observa el estado de sesión reactivamente y redirige al Login si no hay autenticación válida.
 */
@Composable
fun MarketplaceAuthGuard(
    navController: NavController,
    content: @Composable () -> Unit
) {
    val userEmail by AppUsageManager.instance.currentUserEmail.collectAsState()
    val authStateUid by FirebaseProxy.observeAuthState()
        .collectAsState(initial = FirebaseProxy.getCurrentUserId())

    val isAuthenticated = remember(userEmail, authStateUid) {
        val hasValidUid = !authStateUid.isNullOrBlank()
        val isNotAnonymous = !FirebaseProxy.isAnonymous()
        val hasEmail = !userEmail.isNullOrBlank() || !FirebaseProxy.getUserEmail().isNullOrBlank()

        hasValidUid && isNotAnonymous && hasEmail
    }

    LaunchedEffect(isAuthenticated) {
        if (!isAuthenticated) {
            navController.navigate(Routes.login(register = false)) {
                popUpTo(Routes.INICIO) { inclusive = false }
            }
        }
    }

    if (isAuthenticated) {
        content()
    }
}

/**
 * Función de extensión aditiva para registrar rutas protegidas del Marketplace en el NavGraph.
 */
fun NavGraphBuilder.protectedMarketplaceComposable(
    route: String,
    arguments: List<NamedNavArgument> = emptyList(),
    deepLinks: List<NavDeepLink> = emptyList(),
    navController: NavController,
    content: @Composable (NavBackStackEntry) -> Unit
) {
    composable(
        route = route,
        arguments = arguments,
        deepLinks = deepLinks
    ) { backStackEntry ->
        MarketplaceAuthGuard(navController = navController) {
            content(backStackEntry)
        }
    }
}
